import React, { useState, useRef, useEffect } from 'react';
import { Slide, SlideImage } from '../types';
import { useShop } from '../contexts/ShopContext';
import { slideApiService } from '../services/slideApiService';
import { shopLocationService } from '../services/shopLocationService';
import AuthenticatedImage from './AuthenticatedImage';
import SlideViewModal from './SlideViewModal';
import AddToCollectionButton from './AddToCollectionButton';
import SlideCollectionsPickerModal from './SlideCollectionsPickerModal';
import FavoriteButton from './FavoriteButton';
import { formatDateTime } from '../utils/dateUtils';

// Module-level cache so all SlideCards share a single API call per shop
const locationCachePromises: Record<string, Promise<any[]>> = {};
const getShopLocationsCached = (shopId: string): Promise<any[]> => {
  if (!locationCachePromises[shopId]) {
    locationCachePromises[shopId] = shopLocationService.getShopLocations(shopId, 0, 100, 'name', 'ASC');
  }
  return locationCachePromises[shopId];
};

export const invalidateLocationCache = (shopId?: string) => {
  if (shopId) {
    delete locationCachePromises[shopId];
  } else {
    Object.keys(locationCachePromises).forEach(key => delete locationCachePromises[key]);
  }
};

interface SlideCardProps {
  slide: Slide;
  onSlideUpdated?: (slide: Slide) => void;
  onSlideDeleted?: (slideId: string) => void;
  onSlideEdited?: (slide: Slide) => void;
  onFilterByLocation?: (locationId?: string, placeId?: string) => void;
  showActions?: boolean;
  isAnalyzing?: boolean;
}

const SlideCard: React.FC<SlideCardProps> = ({
  slide,
  onSlideUpdated,
  onSlideDeleted,
  onSlideEdited,
  onFilterByLocation,
  showActions = true,
  isAnalyzing = false
}) => {
  const { canManageSlides, canViewPriceData, isInternalMember } = useShop();
  const [showViewModal, setShowViewModal] = useState(false);
  const [showCollectionPicker, setShowCollectionPicker] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [imageHovered, setImageHovered] = useState(false);
  const [prefetchTriggered, setPrefetchTriggered] = useState(false);
  const [resolvedLocationName, setResolvedLocationName] = useState<string | undefined>(undefined);
  const [resolvedPlaceName, setResolvedPlaceName] = useState<string | undefined>(undefined);
  const [isInViewport, setIsInViewport] = useState(false);
  const [heicPolling, setHeicPolling] = useState(false);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  // HEIC polling: periodically re-fetch slide data to pick up new image ID after conversion
  useEffect(() => {
    if (!heicPolling) return;
    const interval = setInterval(async () => {
      try {
        const updated = await slideApiService.getSlideById(slide.shopId, slide.id);
        const oldIds = (slide.images || []).map(img => img.id).sort().join(',');
        const newIds = (updated.images || []).map((img: SlideImage) => img.id).sort().join(',');
        if (oldIds !== newIds) {
          setHeicPolling(false);
          onSlideUpdated?.({ ...slide, images: updated.images || [] });
        }
      } catch { }
    }, 3000);
    const timeout = setTimeout(() => setHeicPolling(false), 120000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [heicPolling, slide, onSlideUpdated]);

  // Detect touch device (no hover capability)
  const isTouchDevice = typeof window !== 'undefined' && window.matchMedia('(hover: none)').matches;

  // IntersectionObserver: show arrows + prefetch images when card is in viewport on touch devices
  useEffect(() => {
    if (!isTouchDevice || !imageContainerRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInViewport(entry.isIntersecting);
        if (entry.isIntersecting) setPrefetchTriggered(true);
      },
      { threshold: 0.5 }
    );
    observer.observe(imageContainerRef.current);
    return () => observer.disconnect();
  }, [isTouchDevice]);

  // Resolve location/place names from IDs
  useEffect(() => {
    if (!slide.locationId && !slide.placeId) return;

    getShopLocationsCached(slide.shopId).then(locations => {
      if (slide.placeId) {
        for (const location of locations) {
          const place = location.places?.find((p: any) => p.id === slide.placeId);
          if (place) {
            setResolvedLocationName(location.name);
            setResolvedPlaceName(place.name);
            return;
          }
        }
      } else if (slide.locationId) {
        const location = locations.find((l: any) => l.id === slide.locationId);
        if (location) {
          setResolvedLocationName(location.name);
        }
      }
    }).catch(err => console.error('Failed to resolve location:', err));
  }, [slide.shopId, slide.locationId, slide.placeId]);

  // Swipe/drag refs (using refs to avoid re-renders on every move event)
  const swipedRef = useRef(false);
  const touchStartRef = useRef<number | null>(null);
  const touchEndRef = useRef<number | null>(null);
  const dragStartRef = useRef<number | null>(null);
  const isDraggingRef = useRef(false);
  const minSwipeDistance = 50;

  const sortedImages = slide.images?.length > 0
    ? [...slide.images].sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  const hasMultipleImages = sortedImages.length > 1;
  const currentImage = sortedImages[currentImageIndex] || null;

  // Show arrows on hover (desktop) or when in viewport (touch devices)
  const showArrows = imageHovered && !isTouchDevice;

  const triggerPrefetch = () => {
    if (!prefetchTriggered && hasMultipleImages) setPrefetchTriggered(true);
  };

  const handlePrevImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
  };

  const handleNextImage = (e: React.MouseEvent) => {
    e.stopPropagation();
    triggerPrefetch();
    setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
  };

  // Touch swipe handlers (mobile)
  const onTouchStart = (e: React.TouchEvent) => {
    touchEndRef.current = null;
    touchStartRef.current = e.targetTouches[0].clientX;
  };

  const onTouchMove = (e: React.TouchEvent) => {
    touchEndRef.current = e.targetTouches[0].clientX;
  };

  const onTouchEnd = () => {
    if (touchStartRef.current === null || touchEndRef.current === null || !hasMultipleImages) {
      touchStartRef.current = null;
      touchEndRef.current = null;
      return;
    }
    const distance = touchStartRef.current - touchEndRef.current;
    if (distance > minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
      swipedRef.current = true;
    } else if (distance < -minSwipeDistance) {
      triggerPrefetch();
      setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
      swipedRef.current = true;
    }
    touchStartRef.current = null;
    touchEndRef.current = null;
  };

  // Mouse drag handlers (desktop click-drag)
  const onDragStart = (e: React.MouseEvent) => {
    dragStartRef.current = e.clientX;
    isDraggingRef.current = false;
  };

  const onDragMove = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && Math.abs(e.clientX - dragStartRef.current) > 5) {
      isDraggingRef.current = true;
    }
  };

  const onDragEnd = (e: React.MouseEvent) => {
    if (dragStartRef.current !== null && isDraggingRef.current && hasMultipleImages) {
      const distance = dragStartRef.current - e.clientX;
      if (distance > minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === sortedImages.length - 1 ? 0 : prev + 1);
        swipedRef.current = true;
      } else if (distance < -minSwipeDistance) {
        triggerPrefetch();
        setCurrentImageIndex(prev => prev === 0 ? sortedImages.length - 1 : prev - 1);
        swipedRef.current = true;
      }
    }
    dragStartRef.current = null;
    isDraggingRef.current = false;
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      try {
        await slideApiService.deleteSlide(slide.shopId, slide.id);
        onSlideDeleted?.(slide.id);
      } catch (error) {
        console.error('Failed to delete slide:', error);
        alert('Failed to delete slide. Please try again.');
      }
    }
  };

  const formatPrice = (price: number | undefined): string => {
    if (price === undefined || price === null) return 'Price not set';
    return `$${price.toLocaleString()}`;
  };

  return (
    <>
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        // Inactive: golden frame around the whole card (image + info). Internal-only, like the dots.
        border: isInternalMember && !slide.active ? '3px solid var(--warning)' : '1px solid var(--border-light)',
        borderRadius: '8px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(139, 69, 19, 0.1)',
        transition: 'all 0.15s ease',
        cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.boxShadow = '0 4px 6px rgba(139, 69, 19, 0.1), 0 2px 4px rgba(139, 69, 19, 0.06)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.boxShadow = '0 1px 3px rgba(139, 69, 19, 0.1)';
      }}
    >
      <div
        ref={imageContainerRef}
        style={{
          position: 'relative',
          cursor: currentImage ? 'pointer' : 'default',
          touchAction: hasMultipleImages ? 'pan-y' : 'auto',
          userSelect: 'none',
          WebkitUserSelect: 'none'
        }}
        onMouseEnter={() => {
          setImageHovered(true);
          triggerPrefetch();
        }}
        onMouseLeave={() => {
          setImageHovered(false);
          dragStartRef.current = null;
          isDraggingRef.current = false;
        }}
        onTouchStart={hasMultipleImages ? onTouchStart : undefined}
        onTouchMove={hasMultipleImages ? onTouchMove : undefined}
        onTouchEnd={hasMultipleImages ? onTouchEnd : undefined}
        onMouseDown={hasMultipleImages ? onDragStart : undefined}
        onMouseMove={hasMultipleImages ? onDragMove : undefined}
        onMouseUp={hasMultipleImages ? onDragEnd : undefined}
        onClick={() => {
          if (swipedRef.current) {
            swipedRef.current = false;
            return;
          }
          if (currentImage) {
            setShowViewModal(true);
          }
        }}
      >
        {sortedImages.length > 0 ? (
          sortedImages.map((img, index) => {
            if (index !== currentImageIndex && !prefetchTriggered) return null;
            return (
              <AuthenticatedImage
                key={img.id}
                shopId={slide.shopId}
                slideId={slide.id}
                imageId={img.id}
                alt={slide.name}
                loading={index === currentImageIndex ? 'eager' : (prefetchTriggered ? 'eager' : 'lazy')}
                size="thumb_300"
                onHeicPending={() => setHeicPolling(true)}
                style={{
                  width: '100%',
                  aspectRatio: '4/3',
                  objectFit: 'cover',
                  pointerEvents: 'none',
                  ...(index !== currentImageIndex ? { position: 'absolute', visibility: 'hidden' } : {})
                }}
              />
            );
          })
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              aspectRatio: '4/3',
              backgroundColor: 'var(--bg-secondary)',
              color: 'var(--text-secondary)'
            }}
          >
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '8px' }}>🖼️</div>
              <div style={{ fontSize: '0.875rem' }}>No Image</div>
            </div>
          </div>
        )}

        {/* Sold Indicator — black "$" on a white chip (top-left). active/sold are shop-internal
            state; never shown to external (CLIENT/VENDOR) members (projection omits these fields). */}
        {isInternalMember && slide.sold && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              left: '8px',
              width: '20px',
              height: '20px',
              borderRadius: '50%',
              backgroundColor: 'var(--bg-primary)',
              color: 'var(--text-primary)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 800,
              fontSize: '13px',
              lineHeight: 1,
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.4)'
            }}
            title="Sold"
          >$</div>
        )}

        {/* Inactive is shown as the golden frame on the outer card (see the card's border above). */}

        {/* Published Indicator (bottom-left) — internal-only, mirrors the sold (top-left) and
            inactive (top-right) status dots. */}
        {isInternalMember && slide.published && (
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '8px',
              width: '14px',
              height: '14px',
              borderRadius: '50%',
              backgroundColor: 'var(--primary-light)',
              border: '2px solid rgba(255, 255, 255, 0.8)',
              zIndex: 1,
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.3)'
            }}
            title="Published"
          />
        )}

        {/* Carousel Navigation Arrows */}
        {hasMultipleImages && (
          <>
            <button
              onClick={handlePrevImage}
              style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                color: 'var(--text-primary)',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                opacity: showArrows ? 0.9 : 0,
                transition: 'opacity 0.2s ease',
                zIndex: 1,
                padding: 0,
                lineHeight: 1,
                WebkitTapHighlightColor: 'transparent'
              }}
              aria-label="Previous image"
            >
              ‹
            </button>
            <button
              onClick={handleNextImage}
              style={{
                position: 'absolute',
                right: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                background: 'rgba(255, 255, 255, 0.8)',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                fontSize: '18px',
                color: 'var(--text-primary)',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.2)',
                opacity: showArrows ? 0.9 : 0,
                transition: 'opacity 0.2s ease',
                zIndex: 1,
                padding: 0,
                lineHeight: 1,
                WebkitTapHighlightColor: 'transparent'
              }}
              aria-label="Next image"
            >
              ›
            </button>
          </>
        )}

        {/* Dot Indicators */}
        {hasMultipleImages && (
          <div
            style={{
              position: 'absolute',
              bottom: '8px',
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              borderRadius: '12px',
              backgroundColor: 'rgba(0, 0, 0, 0.4)',
              zIndex: 1
            }}
          >
            {sortedImages.slice(0, 7).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: index === currentImageIndex ? 'var(--bg-card)'
                    : (sortedImages.length > 7 && currentImageIndex >= 7 && index === 6) ? 'white'
                    : 'rgba(255, 255, 255, 0.5)',
                  transition: 'background-color 0.2s ease'
                }}
              />
            ))}
          </div>
        )}

        <AddToCollectionButton visible={imageHovered} onClick={() => setShowCollectionPicker(true)} />
        <FavoriteButton slideId={slide.id} />
      </div>

      <div style={{ padding: '12px 16px' }}>
        <h3
          style={{
            margin: 0,
            fontSize: '1rem',
            fontWeight: 600,
            marginBottom: '4px',
            color: 'var(--text-primary)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maskImage: 'linear-gradient(to right, black 80%, transparent 100%)',
            WebkitMaskImage: 'linear-gradient(to right, black 80%, transparent 100%)'
          }}
        >
          {slide.name}
        </h3>

        {/* AI Analysis in-progress indicator */}
        {isAnalyzing && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '8px',
              padding: '4px 8px',
              backgroundColor: 'var(--bg-secondary)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              color: 'var(--text-secondary)'
            }}
          >
            <span
              style={{
                display: 'inline-block',
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: 'var(--primary)',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}
            />
            Analyzing...
          </div>
        )}

        {/* Location/Place - right under title, clickable to filter */}
        {(resolvedLocationName || resolvedPlaceName) && (
          <div
            style={{
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              cursor: onFilterByLocation ? 'pointer' : 'default',
              borderRadius: '4px',
              padding: '2px 4px',
              marginLeft: '-4px',
              transition: 'background-color 0.15s ease'
            }}
            onClick={(e) => {
              if (onFilterByLocation) {
                e.stopPropagation();
                onFilterByLocation(slide.locationId, slide.placeId);
              }
            }}
            onMouseEnter={(e) => {
              if (onFilterByLocation) e.currentTarget.style.backgroundColor = 'rgba(139, 134, 128, 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <span
              style={{
                fontSize: '0.75rem',
                color: 'var(--text-secondary)',
                fontWeight: 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {resolvedPlaceName
                ? `${resolvedLocationName} :: ${resolvedPlaceName}`
                : resolvedLocationName}
            </span>
          </div>
        )}

        {/* Metadata — the created date is shop-internal (external projection omits createdAt anyway),
            so it's shown only to internal members. */}
        {isInternalMember && (
          <div
            style={{
              fontSize: '0.75rem',
              color: 'var(--text-secondary)',
              marginBottom: '8px'
            }}
          >
            <div>Created {formatDateTime(slide.createdAt)}</div>
          </div>
        )}

        {/* Actions */}
        {showActions && canManageSlides && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={() => onSlideEdited?.(slide)}
              disabled={isAnalyzing}
              title={isAnalyzing ? 'Edit is disabled while AI analysis is in progress' : undefined}
              style={{
                flex: 1,
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: `1px solid ${isAnalyzing ? 'var(--border-light)' : 'var(--border-medium)'}`,
                color: isAnalyzing ? 'var(--primary-light)' : 'var(--text-primary)',
                borderRadius: '4px',
                cursor: isAnalyzing ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.15s ease',
                opacity: isAnalyzing ? 0.6 : 1
              }}
              onMouseEnter={(e) => {
                if (!isAnalyzing) e.currentTarget.style.backgroundColor = 'var(--bg-primary)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Edit
            </button>
            <button
              onClick={handleDelete}
              style={{
                padding: '6px 12px',
                backgroundColor: 'transparent',
                border: '1px solid var(--error)',
                color: 'var(--error-text)',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '0.875rem',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'var(--error-bg)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              Delete
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Quick add to collections; rendered outside the card so it can't trigger the card's handlers. */}
    {showCollectionPicker && (
      <SlideCollectionsPickerModal slide={slide} onClose={() => setShowCollectionPicker(false)} />
    )}

    {/* Slide View Modal */}
    {showViewModal && (
      <SlideViewModal
        slide={slide}
        onClose={() => setShowViewModal(false)}
        onEdit={(slide) => {
          setShowViewModal(false);
          onSlideEdited?.(slide);
        }}
      />
    )}
    </>
  );
};

export default SlideCard;